import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Trophy,
  Crown,
  Medal,
  Flame,
  TrendingUp,
  TrendingDown,
  Calendar,
  Activity,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

import { API_BASE } from "../lib/api"; 
import { safeJson } from "../lib/api";
const PODIUM_STYLES = [
  { // 1st
    icon: Crown,
    border: "border-amber-300/60",
    glow: "shadow-[0_0_50px_rgba(255,215,0,0.35)]",
    chip: "bg-gradient-to-br from-amber-300 to-orange-500 text-black",
    accent: "#ffd700",
    label: "1ST",
  },
  { // 2nd
    icon: Medal,
    border: "border-slate-300/40",
    glow: "shadow-[0_0_30px_rgba(203,213,225,0.25)]",
    chip: "bg-gradient-to-br from-slate-200 to-slate-400 text-black",
    accent: "#cbd5e1",
    label: "2ND",
  },
  { // 3rd
    icon: Medal,
    border: "border-orange-400/40",
    glow: "shadow-[0_0_30px_rgba(251,146,60,0.25)]",
    chip: "bg-gradient-to-br from-orange-300 to-amber-700 text-black",
    accent: "#fb923c",
    label: "3RD",
  },
];

export default function SimulatorLeaderboardPage() {
  const { lang } = useLanguage();
  const [period, setPeriod] = useState("weekly");
  const [items, setItems] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const API = API_BASE;  // runtime-resolved, see /lib/api.js
        const res = await fetch(`${API}/api/simulator/leaderboard?period=${period}&limit=10`);
        const data = await safeJson(res);
        setItems(data.items || []);
        setTotalSessions(data.total_sessions || 0);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [period]);

  const podium = items.slice(0, 3);
  const rest = items.slice(3);

  const t = {
    es: {
      eyebrow: "Competencia global",
      title: "Leaderboard",
      subtitle: "Los traders RE₿EL con mejor PnL en cada simulación. ¿Puedes superarlos?",
      weekly: "Semanal",
      all: "Histórico",
      sessions: "Sesiones",
      best_pnl: "Mejor PnL",
      empty: "Aún no hay sesiones registradas. Sé el primero.",
      rank: "Rango",
      no_drawdown: "Sin DD",
    },
    en: {
      eyebrow: "Global competition",
      title: "Leaderboard",
      subtitle: "RE₿EL traders with the best PnL each simulation. Can you beat them?",
      weekly: "Weekly",
      all: "All-time",
      sessions: "Sessions",
      best_pnl: "Best PnL",
      empty: "No sessions recorded yet. Be the first.",
      rank: "Rank",
      no_drawdown: "No DD",
    },
    sk: {
      eyebrow: "Globálna súťaž",
      title: "Leaderboard",
      subtitle: "RE₿EL traderi s najlepším PnL každú simuláciu. Dokážeš ich poraziť?",
      weekly: "Týždenný",
      all: "Celkový",
      sessions: "Sessions",
      best_pnl: "Najlepší PnL",
      empty: "Zatiaľ žiadne nahrané relácie. Buď prvý.",
      rank: "Rank",
      no_drawdown: "Bez DD",
    }, de: { eyebrow: "Globaler Wettbewerb", title: "Leaderboard", subtitle: "Die RE₿EL-Trader mit dem besten PnL in jeder Simulation. Kannst du sie übertreffen?", weekly: "Wöchentlich", all: "Historisch", sessions: "Sitzungen", best_pnl: "Bester PnL", empty: "Noch keine Sitzungen registriert. Sei der Erste.", rank: "Rang", no_drawdown: "Ohne DD" }, "zh-CN": { eyebrow: "全球竞赛", title: "排行榜", subtitle: "每次模拟中 PnL 表现最佳的 RE₿EL 交易者。你能超越他们吗？", weekly: "每周", all: "历史", sessions: "交易场次", best_pnl: "最佳 PnL", empty: "尚无记录的交易场次。成为第一个。", rank: "排名", no_drawdown: "无回撤" },
  }[lang] || {};

  return (
    <div className="min-h-screen bg-[#05060d] text-white antialiased" data-testid="sim-leaderboard-page">
      <Navbar />

      <section className="relative w-full overflow-hidden pb-12 pt-32 md:pt-40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-5%,rgba(255,215,0,0.14),transparent_60%)]" />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,215,0,0.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,215,0,0.3) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            maskImage: "radial-gradient(circle at 50% 0%, black, transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-5xl px-6 md:px-12">
          <Link
            to="/simulator"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/50 transition-colors hover:text-white"
            data-testid="leaderboard-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            RE₿EL Market Simulator™
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/10 px-3 py-1.5 backdrop-blur"
          >
            <Trophy className="h-3.5 w-3.5 text-amber-300" />
            <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-200 md:text-xs">
              {t.eyebrow}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-4 font-display text-5xl font-black tracking-[-0.03em] sm:text-6xl md:text-7xl"
            data-testid="leaderboard-title"
          >
            {t.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-4 max-w-2xl text-base text-white/60 md:text-lg"
          >
            {t.subtitle}
          </motion.p>

          {/* Period tabs + stats */}
          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex rounded-full border border-white/10 bg-black/30 p-1 backdrop-blur" data-testid="lb-period-switch">
              {["weekly", "all"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  data-testid={`lb-period-${p}`}
                  className={`relative inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[10px] font-bold uppercase tracking-[0.22em] transition-all ${
                    period === p
                      ? "bg-amber-300 text-black shadow-[0_0_24px_rgba(255,215,0,0.35)]"
                      : "text-white/55 hover:text-white"
                  }`}
                >
                  <Calendar className="h-3 w-3" />
                  {p === "weekly" ? t.weekly : t.all}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-white/45">
              <Activity className="h-3.5 w-3.5" />
              <span>{totalSessions}</span>
              <span>{t.sessions}</span>
            </div>
          </div>

          {/* Podium */}
          {!loading && podium.length > 0 && (
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="lb-podium">
              {podium.map((p, i) => {
                const cfg = PODIUM_STYLES[i];
                const Icon = cfg.icon;
                const positive = p.best_pnl_pct >= 0;
                return (
                  <motion.div
                    key={p.position}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 + i * 0.08 }}
                    className={`relative overflow-hidden rounded-3xl border-2 bg-gradient-to-br from-white/[0.06] via-white/[0.02] to-transparent p-5 backdrop-blur-xl ${cfg.border} ${cfg.glow} ${
                      i === 0 ? "sm:order-2 sm:scale-105" : i === 1 ? "sm:order-1" : "sm:order-3"
                    }`}
                    data-testid={`lb-podium-${i + 1}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${cfg.chip}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="font-display text-3xl font-black tracking-tighter" style={{ color: cfg.accent }}>
                        #{p.position}
                      </span>
                    </div>
                    <p className="mt-4 truncate font-display text-lg font-bold tracking-tight" title={p.display_name}>
                      {p.display_name}
                    </p>
                    <p className="mt-1 truncate text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">
                      {p.scenario_name}
                    </p>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span
                        className={`font-display text-3xl font-black tracking-tighter ${
                          positive ? "text-emerald-300" : "text-rose-300"
                        }`}
                      >
                        {positive ? "+" : ""}
                        {p.best_pnl_pct.toFixed(2)}%
                      </span>
                      {positive ? (
                        <TrendingUp className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-rose-400" />
                      )}
                    </div>
                    <div
                      className="mt-4 inline-block rounded-md border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
                      style={{
                        background:
                          p.rank === "MARKET PREDATOR"
                            ? "linear-gradient(135deg,#ffd700,#ff8c00)"
                            : "rgba(255,255,255,0.04)",
                        color: p.rank === "MARKET PREDATOR" ? "#0a0a10" : "#fff",
                        borderColor: "rgba(255,255,255,0.18)",
                      }}
                    >
                      {p.rank}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Rest of the list */}
          {!loading && rest.length > 0 && (
            <div className="mt-8 space-y-2" data-testid="lb-list">
              {rest.map((p, i) => {
                const positive = p.best_pnl_pct >= 0;
                return (
                  <motion.div
                    key={p.position}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.04 }}
                    className="grid grid-cols-12 items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.025] px-5 py-3 backdrop-blur-md transition-all hover:border-white/20 hover:bg-white/[0.04]"
                    data-testid={`lb-row-${p.position}`}
                  >
                    <span className="col-span-1 font-mono text-base font-bold text-white/45">
                      #{p.position}
                    </span>
                    <div className="col-span-6 min-w-0 sm:col-span-5">
                      <p className="truncate font-display text-sm font-bold tracking-tight">
                        {p.display_name}
                      </p>
                      <p className="mt-0.5 truncate text-[10px] uppercase tracking-[0.18em] text-white/40">
                        {p.scenario_name}
                      </p>
                    </div>
                    <div className="col-span-5 text-right sm:col-span-3">
                      <p
                        className={`font-display text-lg font-black tracking-tighter ${
                          positive ? "text-emerald-300" : "text-rose-300"
                        }`}
                      >
                        {positive ? "+" : ""}
                        {p.best_pnl_pct.toFixed(2)}%
                      </p>
                    </div>
                    <div className="hidden text-right sm:col-span-3 sm:block">
                      <span className="rounded-md border border-white/15 bg-white/[0.04] px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-white/70">
                        {p.rank}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Empty / loading */}
          {loading && (
            <p className="mt-12 font-mono text-xs uppercase tracking-[0.3em] text-white/50">Cargando…</p>
          )}
          {!loading && items.length === 0 && (
            <div className="mt-10 rounded-2xl border border-dashed border-white/10 bg-white/[0.025] p-10 text-center">
              <Flame className="mx-auto h-10 w-10 text-amber-400/70" />
              <p className="mt-3 text-sm text-white/60">{t.empty}</p>
              <Link
                to="/simulator/play"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-black"
                data-testid="lb-empty-cta"
              >
                <Trophy className="h-4 w-4" />
                Reclama el #1
              </Link>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
